import { Routes } from 'config/routes';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Configuration for a tab link - defines what content to load and where to navigate
 */
export interface ITabLinkConfig {
  /** Unique identifier for this tab link content */
  id: string;
  /** Display title for the content section */
  title: string;
  /** Short description of what this content shows */
  description: string;
  /** The route to navigate to (tab target) */
  targetRoute: Routes;
  /** Additional metadata for the content loader */
  metadata?: Record<string, unknown>;
}

/**
 * Content loader hook signature - takes config and returns content data
 */
export type TabContentLoader<T = unknown> = (
  config: ITabLinkConfig
) => ITabContentResult<T>;

/**
 * Result from a tab content loader hook
 */
export interface ITabContentResult<T = unknown> {
  /** Loading state */
  loading: boolean;
  /** Error if any */
  error?: Error;
  /** The loaded data */
  data: T | null;
  /** The original config that triggered this load */
  config: ITabLinkConfig | null;
}

/**
 * Context value interface
 */
export interface ITabLinkContextValue {
  /** Current active tab link config */
  activeConfig: ITabLinkConfig | null;
  /** Navigate to a tab with specific content configuration */
  navigateToTab: (config: ITabLinkConfig) => void;
  /** Clear the active config */
  clearConfig: () => void;
  /** Check if a specific config ID is currently active */
  isActive: (configId: string) => boolean;
}

const TabLinkContext = createContext<ITabLinkContextValue | null>(null);

interface ITabLinkProviderProps {
  children: ReactNode;
}

export const TabLinkProvider: FC<ITabLinkProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [activeConfig, setActiveConfig] = useState<ITabLinkConfig | null>(null);

  const navigateToTab = useCallback(
    (config: ITabLinkConfig) => {
      setActiveConfig(config);
      navigate(config.targetRoute);
    },
    [navigate]
  );

  const clearConfig = useCallback(() => {
    setActiveConfig(null);
  }, []);

  const isActive = useCallback(
    (configId: string) => {
      return activeConfig?.id === configId;
    },
    [activeConfig]
  );

  const contextValue: ITabLinkContextValue = {
    activeConfig,
    navigateToTab,
    clearConfig,
    isActive,
  };

  return (
    <TabLinkContext.Provider value={contextValue}>
      {children}
    </TabLinkContext.Provider>
  );
};

/**
 * Hook to access the tab link context
 */
export const useTabLink = (): ITabLinkContextValue => {
  const context = useContext(TabLinkContext);
  if (!context) {
    throw new Error('useTabLink must be used within a TabLinkProvider');
  }
  return context;
};

/**
 * Hook to get the active tab content config (for use in target tabs)
 */
export const useActiveTabContent = (): ITabLinkConfig | null => {
  const { activeConfig } = useTabLink();
  return activeConfig;
};
